/**
 * Museum About Tabs Component
 * Custom Element: <museum-about-tabs>
 */
if (!customElements.get('museum-about-tabs')) {
  class MuseumAboutTabs extends HTMLElement {
    constructor() {
      super();

      this.tabs = [];
      this.panels = [];
      this.navWrap = null;
      this.handleTabClick = this.onTabClick.bind(this);
      this.handleKeyDown = this.onKeyDown.bind(this);
    }

    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('.museum-about-tabs__tab-btn'));
      this.panels = Array.from(this.querySelectorAll('.museum-about-tabs__panel'));
      this.navWrap = this.querySelector('.museum-about-tabs__nav-wrap');

      if (!this.tabs.length || !this.panels.length) return;

      this.bindEvents();
      this.initDragScroll();
    }

    disconnectedCallback() {
      this.unbindEvents();
    }

    bindEvents() {
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', this.handleTabClick);
        tab.addEventListener('keydown', this.handleKeyDown);
      });
    }

    unbindEvents() {
      this.tabs.forEach((tab) => {
        tab.removeEventListener('click', this.handleTabClick);
        tab.removeEventListener('keydown', this.handleKeyDown);
      });
    }

    initDragScroll() {
      if (!this.navWrap) return;
      const slider = this.navWrap;
      let isDown = false;
      let startX = 0;
      let scrollLeft = 0;

      slider.addEventListener('mousedown', (e) => {
        isDown = true;
        slider.classList.add('is-dragging');
        startX = e.pageX - slider.offsetLeft;
        scrollLeft = slider.scrollLeft;
      });

      slider.addEventListener('mouseleave', () => {
        isDown = false;
        slider.classList.remove('is-dragging');
      });

      slider.addEventListener('mouseup', () => {
        isDown = false;
        slider.classList.remove('is-dragging');
      });

      slider.addEventListener('mousemove', (e) => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX - slider.offsetLeft;
        const walk = (x - startX) * 1.5;
        slider.scrollLeft = scrollLeft - walk;
      });
    }

    onTabClick(event) {
      const button = event.currentTarget;
      const tabNumber = button.dataset.tab;
      if (tabNumber) {
        this.switchTab(tabNumber, button);
      }
    }

    onKeyDown(event) {
      const targetTab = event.currentTarget;
      const currentIndex = this.tabs.indexOf(targetTab);
      let newIndex = currentIndex;

      if (event.key === 'ArrowRight') {
        newIndex = (currentIndex + 1) % this.tabs.length;
      } else if (event.key === 'ArrowLeft') {
        newIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
      } else if (event.key === 'Home') {
        newIndex = 0;
      } else if (event.key === 'End') {
        newIndex = this.tabs.length - 1;
      } else {
        return;
      }

      event.preventDefault();
      const nextTab = this.tabs[newIndex];
      nextTab.focus();
      this.switchTab(nextTab.dataset.tab, nextTab);
    }

    switchTab(tabNumber, targetTab) {
      let activeTabBtn = targetTab;

      // Update Tab Buttons
      this.tabs.forEach((tab) => {
        const isActive = tab.dataset.tab === tabNumber;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        if (isActive) activeTabBtn = tab;
      });

      // Update Tab Panels
      this.panels.forEach((panel) => {
        const panelId = panel.id;
        const isActive = panelId.endsWith(`-${tabNumber}`);
        panel.classList.toggle('is-active', isActive);
      });

      // Auto scroll active tab into view on mobile
      if (activeTabBtn && this.navWrap) {
        try {
          activeTabBtn.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center'
          });
        } catch (e) {
          const navRect = this.navWrap.getBoundingClientRect();
          const tabRect = activeTabBtn.getBoundingClientRect();
          const offset = (tabRect.left - navRect.left) - (navRect.width / 2) + (tabRect.width / 2);
          this.navWrap.scrollLeft += offset;
        }
      }
    }
  }

  customElements.define('museum-about-tabs', MuseumAboutTabs);
}
